import styled from 'styled-components';
import { useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { Sheet, SheetRef } from 'react-modal-sheet';
import FilterWrapper, {
  FilterStyles
} from '../../../search-new/components/filters-menu/FilterWrapper';
import { SearchFiltersTypes } from '../../../../reducers/search-filters/reducer';
import SortIcon from '../../../../new-components/svg-icons/SortIcon';
import {
  SearchContext,
  SearchContextInterface
} from '../../../search-new/context/SearchContext';
import SearchIcon from '../../../../new-components/svg-icons/SearchIcon';
import debounce from '../../../search-new/utils';
import SelectFilterContainer from '../../../search-new/components/filters-menu/select-filter/SelectFilterContainer';

interface FilterChipProps {
  filterType:
    | SearchFiltersTypes
    | SearchFiltersTypes[]
    | { min: SearchFiltersTypes; max: SearchFiltersTypes }
    | { delivery_date: SearchFiltersTypes; delivery_years: SearchFiltersTypes };

  filterName: string;
  filterOptions: any;
  filterStyle: FilterStyles;
  singleSelect?: boolean;
}

const QuickFilter = ({
  filterType,
  filterName,
  filterOptions,
  filterStyle,
  singleSelect
}: FilterChipProps) => {
  const { t } = useTranslation(['search', 'common']);
  const { searchFiltersState } = useContext(
    SearchContext
  ) as SearchContextInterface;
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { locale } = useRouter();
  const handleClick = () => {
    setFilterOpen(true);
  };
  const debouncedResults = useCallback(
    debounce((term: string) => {
      setSearchTerm(term);
    }, 500),
    []
  );

  const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    debouncedResults(e.currentTarget.value);
  };
  const filteredOptions = useCallback(() => {
    if (filterType === SearchFiltersTypes.COMPOUNDS) {
      return filterOptions?.filter((option: any) =>
        option?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else {
      return filterOptions?.filter((option: any) =>
        option?.options?.displayedValue
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }
  }, [searchTerm]);

  const sheetRef = useRef<SheetRef>(null);
  const getFiltersCount = () => {
    let count = 0;
    if (
      !Array.isArray(filterType) &&
      typeof filterType !== 'object' &&
      Array.isArray(searchFiltersState?.searchFilters[filterType])
    ) {
      count = (searchFiltersState.searchFilters[filterType] as Array<any>)
        .length;
    }
    return count > 0 ? <span className="filters-count">{count}</span> : null;
  };

  return (
    <Container onClick={handleClick} id={filterName} className="hide-scrollbar">
      {
        <span>
          {t(filterName)} {getFiltersCount()}
        </span>
      }
      <Sheet
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        detent="content-height"
        className="quickfilter-bottom-sheet hide-scrollbar"
        ref={sheetRef}
        disableScrollLocking
      >
        <Sheet.Backdrop onTap={() => setFilterOpen(false)} />
        <Sheet.Container
          style={{
            padding: '8px 16px 16px 16px',
            backgroundColor: '#FBFCFE'
          }}
          className="hide-scrollbar"
        >
          <Sheet.Header
            style={{
              padding: '16px 0',
              direction: locale == 'ar' ? 'rtl' : 'ltr'
            }}
            className="hide-scrollbar"
          >
            <SearchBar id="search-bar">
              <SearchIcon width="17.5px" height="17.5px" fill="#788496" />

              <Input
                placeholder={t(`${filterType}_search`, { ns: 'search' })}
                onChange={handleInputChange}
              />
            </SearchBar>
          </Sheet.Header>
          <Sheet.Content
            style={{
              width: '100%',
              height: '80vh',
              direction: locale == 'ar' ? 'rtl' : 'ltr'
            }}
            className="hide-scrollbar"
          >
            <Sheet.Scroller
              draggableAt="both"
              style={{
                position: 'relative',
                gap: '16px',
                direction: locale === 'ar' ? 'rtl' : 'ltr'
              }}
              className="hide-scrollbar"
              draggable
            >
              <div className="options-container hide-scrollbar">
                <SelectFilterContainer
                  filterOptions={filteredOptions()}
                  filterType={filterType as SearchFiltersTypes}
                  filterName={filterName}
                  filterStyle={FilterStyles.CHECKBOX}
                  // customAreas={customAreas}
                  // customCompounds={customCompounds}
                  // setCustomCompounds={setCustomCompounds}
                  // setCustomAreas={setCustomAreas}
                />
              </div>
            </Sheet.Scroller>
          </Sheet.Content>
        </Sheet.Container>
      </Sheet>
    </Container>
  );
};

const Container = styled.div`
  &:not(#sort_by) {
    padding: 8px;
    background-color: #f4f9fe;
    color: #1a2028;
    border-radius: 12px;
    font-size: 14px;
    font-family: materialMedium;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  span {
    display: flex;
    gap: 8px;
    .filters-count {
      display: flex;
      width: 20px;
      height: 20px;
      justify-content: center;
      align-items: center;
      border-radius: 50%;
      background: #ff5e00;
      color: #fff;
      font-family: materialMedium;
      font-size: 11px;
    }
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
  padding: 8px 16px;
  gap: 8px;
  border: 1px solid #d3dbe6;
  border-radius: 12px;
  margin-bottom: 12px;
  svg {
    width: 17.5px;
    height: 17.5px;
    fill: #788496;
  }
`;

const Input = styled.input`
  border: none;
  outline: none;
  user-select: none;
  width: 100%;
  background-color: #f7f9fd;

  ::placeholder {
    color: #5b6573;
    font-size: 14px;
    font-family: materialRegular;
  }

  :focus {
    outline: none;
  }
`;
export default QuickFilter;
